import type { Manifest, Series, Course } from './types.js';

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function text(value: unknown, label: string): string {
  if (typeof value !== 'string' || !value.trim() || value !== value.trim() || /[\0\r\n]/.test(value)) {
    throw new Error(`${label} 无效`);
  }
  return value;
}

function resourcePath(value: unknown, label: string): string {
  const path = text(value, label);
  if (path.includes('\\') || path.startsWith('/') || /^[a-z][a-z0-9+.-]*:/i.test(path)) {
    throw new Error(`${label} 必须是系列内的相对路径`);
  }
  if (path.split('/').some((part) => !part || part === '.' || part === '..')) {
    throw new Error(`${label} 包含越界路径`);
  }
  return path;
}

export function validateManifest(value: unknown): Manifest {
  if (!record(value) || value.version !== 2 || !Array.isArray(value.series)) {
    throw new Error('不是有效的 v2 manifest');
  }
  if (typeof value.generatedAt !== 'string' || !Number.isFinite(Date.parse(value.generatedAt))) {
    throw new Error('manifest.generatedAt 无效');
  }
  if (!record(value.source)) throw new Error('manifest.source 缺失');
  const profile = text(value.source.profile, 'manifest.source.profile');
  if (!['development', 'production', 'production-runtime'].includes(profile)) {
    throw new Error(`未知 manifest profile: ${profile}`);
  }

  const seriesIds = new Set<string>();
  for (let seriesIndex = 0; seriesIndex < value.series.length; seriesIndex += 1) {
    const series = value.series[seriesIndex];
    const label = `series[${seriesIndex}]`;
    if (!record(series)) throw new Error(`${label} 无效`);
    const id = text(series.id, `${label}.id`);
    if (/[\\/]/.test(id) || id === '.' || id === '..' || seriesIds.has(id)) throw new Error(`${label}.id 不安全或重复`);
    seriesIds.add(id);
    text(series.title, `${label}.title`);
    const expectedBase = profile === 'development' ? `/media/${id}` : `../${id}`;
    if (series.basePath !== expectedBase) throw new Error(`${label}.basePath 必须是 ${expectedBase}`);
    const flat = Array.isArray(series.courses);
    const chaptered = Array.isArray(series.chapters);
    if (flat === chaptered) throw new Error(`${label} 必须且只能包含 courses 或 chapters`);

    const courses: unknown[] = [];
    if (flat) courses.push(...series.courses as unknown[]);
    if (chaptered) {
      const chapterIds = new Set<string>();
      for (const chapter of series.chapters as unknown[]) {
        if (!record(chapter) || !Array.isArray(chapter.courses)) throw new Error(`${label}.chapters 无效`);
        const chapterId = resourcePath(chapter.id, `${label}.chapter.id`);
        if (chapterIds.has(chapterId)) throw new Error(`${label}.chapter.id 重复`);
        chapterIds.add(chapterId);
        text(chapter.title, `${label}.chapter.title`);
        courses.push(...chapter.courses);
      }
    }
    const courseIds = new Set<string>();
    let enhanced = 0;
    for (const course of courses) {
      if (!record(course)) throw new Error(`${label}.course 无效`);
      const courseId = resourcePath(course.id, `${label}.course.id`);
      if (courseIds.has(courseId)) throw new Error(`${label}.course.id 重复`);
      courseIds.add(courseId);
      text(course.title, `${label}.course.title`);
      const mp3 = resourcePath(course.mp3, `${label}.course.mp3`);
      if (!/\.mp3$/i.test(mp3) || typeof course.hasMeta !== 'boolean') throw new Error(`${label}.course 契约无效`);
      if (course.hasMeta) {
        const meta = resourcePath(course.meta, `${label}.course.meta`);
        if (!/\.enhanced\.json$/i.test(meta)) throw new Error(`${label}.course.meta 不是 enhanced JSON`);
        enhanced += 1;
      } else if (course.meta != null) {
        throw new Error(`${label}.course.meta 与 hasMeta 冲突`);
      }
      // course.topic is optional; when present it must be a {title, summary} object
      if (course.topic != null) {
        if (!record(course.topic)) throw new Error(`${label}.course.topic 必须是对象`);
        text(course.topic.title, `${label}.course.topic.title`);
        text(course.topic.summary, `${label}.course.topic.summary`);
      }
    }
    if (!record(series.metaCoverage) || series.metaCoverage.done !== enhanced || series.metaCoverage.total !== courses.length) {
      throw new Error(`${label}.metaCoverage 与课程不一致`);
    }
    if (series.hasEnhanced !== (enhanced > 0)) throw new Error(`${label}.hasEnhanced 与课程不一致`);
  }
  return value as unknown as Manifest;
}

export async function loadManifest(url = 'series.json'): Promise<Manifest> {
  // The PHP endpoint scans the NAS on every request, so prefer it when the
  // default manifest is requested. A local development checkout may not have
  // the production media directories; in that case its empty result falls
  // back to the checked-in development manifest.
  const urls = url === 'series.json' ? ['/api/manifest.php', url] : [url];

  const errors: string[] = [];
  for (const u of urls) {
    try {
      const resp = await fetch(u, { cache: 'no-cache' });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const manifest = validateManifest(await resp.json());
      if (u === '/api/manifest.php' && manifest.series.length === 0 && urls.length > 1) {
        throw new Error('动态 manifest 为空');
      }
      return manifest;
    } catch (error) {
      errors.push(`${u}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  throw new Error(`Manifest 加载失败（${errors.join('；')}）`);
}

export function findSeries(m: Manifest, id: string): Series | null {
  return m.series.find((s) => s.id === id) ?? null;
}

export function flatCourses(series: Series): Course[] {
  if (series.courses) return series.courses;
  if (series.chapters) return series.chapters.flatMap((c) => c.courses);
  return [];
}

export function findCourse(series: Series, courseId: string): Course | null {
  return flatCourses(series).find((c) => c.id === courseId) ?? null;
}
